import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    FaSearch, FaChartLine, FaGlobe, FaCode, FaLink, FaMobileAlt,
    FaCheckCircle, FaChevronDown, FaChevronUp, FaChevronRight, FaTachometerAlt, FaMapMarkedAlt, FaUserCheck,
    FaChartPie, FaChartBar
} from 'react-icons/fa';
import {
    SiGoogle, SiWordpress
} from 'react-icons/si';
import heroImage from '../../assets/1e9530694cfcc1d368a95d2b3056f69d.jpg';

const SEOServicesPage = () => {
    const [openFaq, setOpenFaq] = useState(null);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const services = [
        { icon: FaSearch, title: 'Keyword Research', desc: 'Identifying high-intent keywords that drive qualified traffic to your website.', image: 'https://images.unsplash.com/photo-1571786256017-aee7a0c009b6?w=600' },
        { icon: FaCode, title: 'Technical SEO', desc: 'Optimizing site architecture, speed, and mobile-friendliness for search crawlers.', image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600' },
        { icon: FaGlobe, title: 'On-Page Optimization', desc: 'Refining content, meta tags, and internal linking for better relevance.', image: 'https://images.unsplash.com/photo-1499750310159-52f0f83ec631?w=600' },
        { icon: FaLink, title: 'Link Building', desc: 'Acquiring high-quality backlinks to boost domain authority and trust.', image: 'https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?w=600' },
        { icon: FaMapMarkedAlt, title: 'Local SEO', desc: 'Dominating local search results and Google My Business to attract nearby customers.', image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=600' },
        { icon: FaChartLine, title: 'SEO Audits', desc: 'Comprehensive analysis of your website health and ranking opportunities.', image: 'https://images.unsplash.com/photo-1551650975-87deedd944c3?w=600' }
    ];

    const tools = [
        { name: 'Search Console', icon: FaSearch, desc: 'Index Status' },
        { name: 'Analytics', icon: FaChartBar, desc: 'Traffic Analysis' },
        { name: 'SEMrush', icon: FaChartPie, desc: 'Competitor Research' },
        { name: 'Ahrefs', icon: FaLink, desc: 'Backlink Analysis' },
        { name: 'Moz', icon: FaGlobe, desc: 'Domain Authority' },
        { name: 'WordPress', icon: SiWordpress, desc: 'CMS Optimization' },
        { name: 'Yoast SEO', icon: FaCode, desc: 'On-Page Tool' },
        { name: 'Schema', icon: FaCode, desc: 'Structured Data' }
    ];

    // Horizontal Flow Steps (SEO Process)
    const flowSteps = [
        { title: 'Audit', icon: FaTachometerAlt, color: 'from-orange-500 to-red-500' },
        { title: 'Research', icon: FaSearch, color: 'from-blue-500 to-cyan-500' },
        { title: 'Optimize', icon: FaCode, color: 'from-green-500 to-emerald-600' },
        { title: 'Content', icon: FaUserCheck, color: 'from-pink-500 to-rose-500' },
        { title: 'Link', icon: FaLink, color: 'from-purple-500 to-violet-500' },
        { title: 'Rank', icon: FaChartLine, color: 'from-yellow-400 to-amber-500' }
    ];

    const faqs = [
        { q: 'Why is SEO important for my business?', a: 'SEO drives organic traffic without paying for ads. It builds long-term credibility and ensures your customers can find you when they search.' },
        { q: 'How long until I see #1 rankings?', a: 'Ranking depend on competition and keyword difficulty. Generally, you can see significant improvement in 3-6 months.' },
        { q: 'Is SEO a one-time process?', a: 'No, SEO requires ongoing optimization. Search algorithms change frequently, and competitors are always trying to outrank you.' },
        { q: 'Do you guarantee #1 ranking?', a: 'No ethical SEO agency guarantees #1 rankings because search algorithms are proprietary. However, we guarantee proven strategies that consistently improve visibility.' },
        { q: 'How does Technical SEO affect rankings?', a: 'Site speed, mobile usability, and secure connections (HTTPS) are direct ranking factors. A technically sound site ranks better.' }
    ];

    const whyChooseUs = [
        { title: 'White Hat Techniques', desc: 'We strictly follow search engine guidelines to ensure sustainable, risk-free growth.' },
        { title: 'Custom Strategy', desc: 'Every business is unique. We tailor our SEO plan to your specific industry and goals.' },
        { title: 'Monthly Reporting', desc: 'Clear, jargon-free reports showing keyword rankings, traffic growth, and conversions.' },
        { title: 'Competitor Analysis', desc: 'We analyze your competitors\' weaknesses and exploit them to your advantage.' },
        { title: 'Content Focus', desc: 'We believe content is king. We help create content that earns backlinks naturally.' },
        { title: 'ROI Focused', desc: 'We focus on keywords that generate revenue, not just vanity traffic metrics.' }
    ];

    return (
        <div className="bg-white font-sans">
            {/* Hero Section */}
            <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url('${heroImage}')` }}
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-green-900/90 via-emerald-900/80 to-transparent"></div>
                </div>

                <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex items-center pt-20">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-2xl text-white"
                    >
                        <p className="text-xs font-light tracking-widest uppercase text-green-300 mb-3">What We Do &gt; Visibility</p>
                        <h1 className="text-4xl md:text-5xl font-light mb-4 leading-tight">Search Engine Optimization</h1>
                        <p className="text-base font-light text-gray-200 leading-relaxed mb-6">
                            Climb the search rankings and connect with customers searching for your services right now.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <a href="#inquiry" className="px-5 py-2 bg-white text-green-700 font-medium rounded-sm hover:shadow-xl transition-all text-sm whitespace-nowrap">
                                Free SEO Audit
                            </a>
                            <a href="#services" className="px-5 py-2 border-2 border-white text-white font-medium rounded-sm hover:bg-white hover:text-green-700 transition-all text-sm whitespace-nowrap">
                                See Our Process
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Intro Stats */}
            <section className="py-20 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="max-w-4xl mx-auto text-center mb-16">
                        <h2 className="text-4xl font-light text-slate-900 mb-6">Dominating Search Results</h2>
                        <p className="text-lg text-slate-600 leading-relaxed">
                            We turn your website into a 24/7 lead generation machine.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-8 text-center divide-x divide-slate-200">
                        {[
                            { label: 'Keywords Ranked #1', val: '5,000+' },
                            { label: 'Organic Traffic', val: '1M+' },
                            { label: 'Conversion Rate', val: '+45%' },
                            { label: 'Client Retention', val: '98%' }
                        ].map((stat, idx) => (
                            <div key={idx} className="p-4">
                                <div className="text-4xl font-bold text-green-600 mb-2">{stat.val}</div>
                                <div className="text-sm text-slate-500 uppercase tracking-wider">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Technologies / Tools */}
            <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-light text-slate-900 mb-6">SEO Toolkit</h2>
                        <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                            Advanced tools to analyze, track, and improve your search performance.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-6">
                        {tools.map((tool, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.05 }}
                                className="p-6 bg-slate-50 rounded-sm border border-slate-200 text-center hover:shadow-lg transition-all group"
                            >
                                <tool.icon className="text-5xl mx-auto mb-4 text-green-600 group-hover:scale-110 transition-transform" />
                                <h3 className="text-lg font-medium text-slate-900 mb-2">{tool.name}</h3>
                                <p className="text-sm text-slate-600">{tool.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Grid (Hover Cards) */}
            <section id="services" className="py-20 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-light text-slate-900 mb-6">SEO Strategies</h2>
                        <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                            Holistic optimization services covering every ranking signal.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {services.map((service, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="group relative h-64 overflow-hidden rounded-sm border border-slate-200 cursor-pointer"
                            >
                                <div className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110" style={{ backgroundImage: `url(${service.image})` }}>
                                    <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/80"></div>
                                </div>
                                <div className="absolute inset-0 p-6 flex flex-col justify-between text-white z-10">
                                    <div>
                                        <service.icon className="text-4xl mb-4 text-green-300" />
                                        <h3 className="text-xl font-medium mb-2">{service.title}</h3>
                                    </div>
                                    <motion.div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-green-600 to-emerald-700 p-6 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                                        <p className="text-white text-sm leading-relaxed">{service.desc}</p>
                                        <button className="mt-4 text-white font-medium hover:underline">Learn More →</button>
                                    </motion.div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="py-20 bg-gradient-to-br from-green-600 via-emerald-600 to-teal-700 text-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-light mb-6">Why Trust Us With Your SEO?</h2>
                        <p className="text-lg text-green-100 max-w-3xl mx-auto">
                            Sustainable results through ethical, data-backed optimization.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {whyChooseUs.map((item, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="bg-white/10 backdrop-blur-sm p-6 rounded-sm border border-white/20 hover:bg-white/20 transition-all"
                            >
                                <FaCheckCircle className="text-3xl text-green-300 mb-4" />
                                <h3 className="text-xl font-medium mb-3">{item.title}</h3>
                                <p className="text-green-100 leading-relaxed">{item.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Timeline (SEO Roadmap with Arrows) */}
            <section className="py-24 bg-slate-50 border-y border-slate-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-20">
                        <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-4">SEO Success Roadmap</h2>
                        <p className="text-slate-600">Our systematic approach to climbing the ranks.</p>
                    </div>

                    <div className="relative">
                        {/* Base Line (Grey) */}
                        <div className="hidden md:block absolute top-[40px] left-0 w-full h-1 bg-slate-200 md:rounded-full -z-0"></div>

                        {/* Steps Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-0 relative">
                            {flowSteps.map((step, idx) => (
                                <div key={idx} className="relative flex flex-col items-center group">

                                    {/* ARROW: Only show BETWEEN steps */}
                                    {idx < flowSteps.length - 1 && (
                                        <div className="hidden md:flex absolute top-[30px] -right-[50%] w-full justify-center items-center z-10 pointer-events-none">
                                            <motion.div
                                                animate={{ x: [-3, 3, -3] }}
                                                transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
                                            >
                                                <FaChevronRight className="text-2xl text-slate-400 font-bold" />
                                            </motion.div>
                                        </div>
                                    )}

                                    {/* Circle Icon */}
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.5, y: 20 }}
                                        whileInView={{ opacity: 1, scale: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: idx * 0.1, type: "spring", stiffness: 100 }}
                                        className="relative z-20"
                                    >
                                        <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center text-white text-3xl shadow-lg mb-6 group-hover:scale-110 transition-transform duration-300 border-4 border-white ring-4 ring-slate-50`}>
                                            <step.icon />
                                        </div>
                                    </motion.div>

                                    {/* Number Badge */}
                                    <div className="absolute top-0 right-1/2 translate-x-10 -translate-y-0 w-7 h-7 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center border-2 border-white shadow-md z-30">
                                        {idx + 1}
                                    </div>

                                    {/* Title */}
                                    <h3 className="text-lg font-bold text-slate-800 group-hover:text-green-600 transition-colors mt-2">{step.title}</h3>

                                    {/* Mobile Vertical Line */}
                                    {idx < flowSteps.length - 1 && (
                                        <div className="md:hidden absolute bottom-[-40px] left-1/2 w-0.5 h-[40px] bg-slate-300"></div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ & Inquiry Form */}
            <section id="inquiry" className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid lg:grid-cols-2 gap-12">

                        {/* FAQs */}
                        <div>
                            <h2 className="text-3xl font-light text-slate-900 mb-8">SEO FAQs</h2>
                            <div className="space-y-4">
                                {faqs.map((faq, idx) => (
                                    <motion.div
                                        key={idx}
                                        initial={{ opacity: 0, y: 10 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: idx * 0.05 }}
                                        className="border border-slate-200 rounded-sm overflow-hidden"
                                    >
                                        <button
                                            onClick={() => toggleFaq(idx)}
                                            className="w-full px-6 py-4 text-left flex justify-between items-center bg-slate-50 hover:bg-slate-100 transition-colors"
                                        >
                                            <span className="font-medium text-slate-900">{faq.q}</span>
                                            {openFaq === idx ? <FaChevronUp className="text-green-600" /> : <FaChevronDown className="text-slate-400" />}
                                        </button>
                                        {openFaq === idx && (
                                            <div className="px-6 py-4 bg-white">
                                                <p className="text-slate-600 leading-relaxed">{faq.a}</p>
                                            </div>
                                        )}
                                    </motion.div>
                                ))}
                            </div>
                        </div>

                        {/* Inquiry Form */}
                        <div className="bg-slate-50 p-8 rounded-sm border border-slate-200">
                            <h2 className="text-3xl font-light text-slate-900 mb-2">Get Your Ranking Report</h2>
                            <p className="text-slate-600 mb-6">Enter your details for a free competitor analysis.</p>

                            <form className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Website URL</label>
                                    <input type="url" className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-green-600" placeholder="https://yourwebsite.com" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
                                        <input type="text" className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-green-600" placeholder="John Doe" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                                        <input type="email" className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-green-600" placeholder="john@example.com" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Top Competitor (Optional)</label>
                                    <input type="text" className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-green-600" placeholder="competitor.com" />
                                </div>

                                <button type="submit" className="w-full bg-green-600 text-white font-medium py-3 rounded-sm hover:bg-green-700 transition-colors shadow-lg">
                                    Analyze My Site
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            {/* Bottom CTA */}
            <section className="py-20 bg-gradient-to-r from-green-700 to-emerald-800 text-white">
                <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
                    <h2 className="text-4xl md:text-5xl font-light mb-6">Ready to Be Found First?</h2>
                    <p className="text-xl font-light mb-8 text-green-100">
                        Stop losing customers to competitors who rank higher than you.
                    </p>
                    <div className="flex flex-wrap gap-3 justify-center">
                        <a href="#inquiry" className="inline-block px-6 py-2.5 bg-white text-green-800 font-medium rounded-sm hover:shadow-xl transition-all text-sm whitespace-nowrap">
                            Rank #1 Today
                        </a>
                        <a href="tel:+919876543210" className="inline-block px-6 py-2.5 border-2 border-white text-white font-medium rounded-sm hover:bg-white hover:text-green-800 transition-all text-sm whitespace-nowrap">
                            Talk to SEO Expert
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default SEOServicesPage;
